'use client';

import dynamic from 'next/dynamic';

import { STATE_CHART_VIEWPORT_ROOT_MARGIN } from '@/constants/stateCharts';
import { useInView } from '@/hooks/useInView';

import styles from '../ChartPlaceholder.module.css';

import type { ComponentType, CSSProperties } from 'react';

type ChartIslandStyleVars = CSSProperties & {
  '--chart-aspect-ratio'?: string;
  '--chart-aspect-ratio-mobile'?: string;
  '--chart-max-width'?: string;
  '--chart-max-width-2xl'?: string;
};

type CreateChartIslandOptions<P extends object> = {
  /** Resolves to the chart component (typically `import('./X').then(m => m.X)`). */
  loader: () => Promise<ComponentType<P>>;
  loadingAriaLabel: string;
  styleVars: ChartIslandStyleVars;
  /** Defer the dynamic chart until near the viewport (sticky once). */
  viewportMargin?: string;
};

/**
 * Factory for below-fold State chart islands: `next/dynamic` + aspect-ratio
 * placeholder, viewport-gated so visx/motion chunks stay off the critical path
 * until the plate is near the viewport.
 */
export function createChartIsland<P extends object>({
  loader,
  loadingAriaLabel,
  styleVars,
  viewportMargin = STATE_CHART_VIEWPORT_ROOT_MARGIN,
}: CreateChartIslandOptions<P>): ComponentType<P> {
  function LoadingFallback() {
    return (
      <div
        className={styles.root}
        style={styleVars}
        role="img"
        aria-busy="true"
        aria-label={loadingAriaLabel}
      />
    );
  }

  const ChartDynamic = dynamic(loader, {
    ssr: false,
    loading: () => <LoadingFallback />,
  });

  function ChartIsland(props: P) {
    const { ref, inView } = useInView<HTMLDivElement>({
      once: true,
      margin: viewportMargin,
    });

    return (
      <div ref={ref}>
        {inView ? <ChartDynamic {...props} /> : <LoadingFallback />}
      </div>
    );
  }

  return ChartIsland;
}
